/**
 * Player Avatar Service
 * Handles player avatar image uploads and URL generation for Supabase Storage
 * Based on clubBannerService.js pattern
 */

import { supabase } from '../utils/supabase';

const PLAYER_AVATARS_BUCKET = 'player-avatars';
const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const MAX_IMAGE_DIMENSION = 4000; // Maximum width or height in pixels
const MIN_AVATAR_SIZE = 100; // Minimum dimension for avatar

// Magic numbers (file signatures) for image formats
const MAGIC_NUMBERS = {
  jpeg: [0xFF, 0xD8, 0xFF],
  png: [0x89, 0x50, 0x4E, 0x47, 0x0D, 0x0A, 0x1A, 0x0A],
  webp: [0x52, 0x49, 0x46, 0x46] // RIFF header, followed by WEBP at bytes 8-11
};

/**
 * Get the public URL for a player avatar image
 * @param {string|null} path - Storage path (e.g., "{playerId}.jpg")
 * @param {boolean} bustCache - Add timestamp to bust browser cache (default: true)
 * @returns {string|null} - Public URL or null if path is empty
 */
export function getPlayerAvatarPublicUrl(path, bustCache = true) {
  if (!path || path.trim() === '') {
    return null;
  }

  try {
    const { data } = supabase
      .storage
      .from(PLAYER_AVATARS_BUCKET)
      .getPublicUrl(path);
    
    if (!data?.publicUrl) {
      return null;
    }

    // Add cache-busting timestamp to force browser to reload image
    if (bustCache) {
      const separator = data.publicUrl.includes('?') ? '&' : '?';
      return `${data.publicUrl}${separator}t=${Date.now()}`;
    }
    
    return data.publicUrl;
  } catch (error) {
    console.error('Error getting player avatar public URL:', error);
    return null;
  }
}

/**
 * Verify file magic numbers (file signature)
 * This prevents uploading of files with fake extensions
 * @param {File} file - File to verify
 * @returns {Promise<{ valid: boolean, detectedType?: string, error?: string }>}
 */
async function verifyFileMagicNumbers(file) {
  try {
    // Read first 12 bytes (enough for all our formats)
    const buffer = await file.slice(0, 12).arrayBuffer();
    const bytes = new Uint8Array(buffer);

    // Check JPEG (FF D8 FF)
    if (bytes[0] === MAGIC_NUMBERS.jpeg[0] && 
        bytes[1] === MAGIC_NUMBERS.jpeg[1] && 
        bytes[2] === MAGIC_NUMBERS.jpeg[2]) {
      return { valid: true, detectedType: 'image/jpeg' };
    }

    // Check PNG (89 50 4E 47 0D 0A 1A 0A)
    if (bytes[0] === MAGIC_NUMBERS.png[0] && 
        bytes[1] === MAGIC_NUMBERS.png[1] && 
        bytes[2] === MAGIC_NUMBERS.png[2] && 
        bytes[3] === MAGIC_NUMBERS.png[3] &&
        bytes[4] === MAGIC_NUMBERS.png[4] &&
        bytes[5] === MAGIC_NUMBERS.png[5] &&
        bytes[6] === MAGIC_NUMBERS.png[6] &&
        bytes[7] === MAGIC_NUMBERS.png[7]) {
      return { valid: true, detectedType: 'image/png' };
    }

    // Check WebP (RIFF....WEBP)
    if (bytes[0] === MAGIC_NUMBERS.webp[0] && 
        bytes[1] === MAGIC_NUMBERS.webp[1] && 
        bytes[2] === MAGIC_NUMBERS.webp[2] && 
        bytes[3] === MAGIC_NUMBERS.webp[3] &&
        bytes[8] === 0x57 && // W
        bytes[9] === 0x45 && // E
        bytes[10] === 0x42 && // B
        bytes[11] === 0x50) { // P
      return { valid: true, detectedType: 'image/webp' };
    }

    return { 
      valid: false, 
      error: 'File does not match any allowed image format. The file may be corrupted or not a real image.' 
    };
  } catch (error) {
    console.error('Error verifying file magic numbers:', error);
    return { 
      valid: false, 
      error: 'Failed to verify file format' 
    };
  }
}

/**
 * Validate image dimensions
 * Prevents uploading of extremely large images or images too small for avatars
 * @param {File} file - File to validate
 * @returns {Promise<{ valid: boolean, width?: number, height?: number, error?: string }>}
 */
async function validateImageDimensions(file) {
  try {
    // Create an image bitmap to check dimensions
    const imageBitmap = await createImageBitmap(file);
    
    if (imageBitmap.width > MAX_IMAGE_DIMENSION || imageBitmap.height > MAX_IMAGE_DIMENSION) {
      imageBitmap.close();
      return {
        valid: false,
        width: imageBitmap.width,
        height: imageBitmap.height,
        error: `Image dimensions (${imageBitmap.width}x${imageBitmap.height}) exceed maximum allowed (${MAX_IMAGE_DIMENSION}x${MAX_IMAGE_DIMENSION})`
      };
    }

    // Check for images too small for avatars
    if (imageBitmap.width < MIN_AVATAR_SIZE || imageBitmap.height < MIN_AVATAR_SIZE) {
      imageBitmap.close();
      return {
        valid: false,
        width: imageBitmap.width,
        height: imageBitmap.height,
        error: `Image is too small for an avatar (minimum ${MIN_AVATAR_SIZE}x${MIN_AVATAR_SIZE} pixels)`
      };
    }

    const dimensions = {
      width: imageBitmap.width,
      height: imageBitmap.height
    };

    imageBitmap.close();
    return { valid: true, ...dimensions };
  } catch (error) {
    console.error('Error validating image dimensions:', error);
    return {
      valid: false,
      error: 'Failed to load image. The file may be corrupted or not a valid image.'
    };
  }
}

/**
 * Validate a file for avatar upload
 * @param {File} file - File to validate
 * @returns {{ valid: boolean, error?: string }}
 */
function validateAvatarFile(file) {
  if (!file) {
    return { valid: false, error: 'No file provided' };
  }

  // Check file type (first line of defense)
  if (!ALLOWED_TYPES.includes(file.type.toLowerCase())) {
    return { 
      valid: false, 
      error: `Invalid file type. Allowed types: ${ALLOWED_TYPES.join(', ')}` 
    };
  }

  // Check file size
  if (file.size > MAX_FILE_SIZE) {
    return { 
      valid: false, 
      error: `File size exceeds ${MAX_FILE_SIZE / (1024 * 1024)}MB limit` 
    };
  }

  // Check for suspiciously small files
  if (file.size < 1000) { // Less than 1KB
    return {
      valid: false,
      error: 'File is too small to be a valid image'
    };
  }

  return { valid: true };
}

/**
 * Optional: Resize and compress an image file using canvas
 * @param {File} file - Original file
 * @param {number} maxDimension - Maximum width or height
 * @param {number} quality - JPEG quality (0-1)
 * @returns {Promise<Blob>} - Resized image blob
 */
async function resizeImage(file, maxDimension = 400, quality = 0.9) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions
        let width = img.width;
        let height = img.height;
        
        if (width > maxDimension || height > maxDimension) {
          if (width > height) {
            height = (height / width) * maxDimension;
            width = maxDimension;
          } else {
            width = (width / height) * maxDimension;
            height = maxDimension;
          }
        }
        
        // Create canvas and resize
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        
        // Convert to blob
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(blob);
            } else {
              reject(new Error('Failed to create blob from canvas'));
            }
          },
          'image/jpeg',
          quality
        );
      };
      
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = e.target.result;
    };
    
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsDataURL(file);
  });
}

/**
 * Upload a player avatar image to Supabase Storage
 * @param {string} playerId - Player UUID
 * @param {File} file - Image file to upload
 * @param {Object} options - Upload options
 * @param {boolean} options.resize - Whether to resize the image (default: true)
 * @param {number} options.maxDimension - Max dimension for resize (default: 400)
 * @param {number} options.quality - JPEG quality 0-1 (default: 0.9)
 * @returns {Promise<{ success: boolean, path?: string, publicUrl?: string, error?: string }>}
 */
export async function uploadPlayerAvatar(
  playerId, 
  file, 
  options = { resize: true, maxDimension: 400, quality: 0.9 }
) {
  try {
    // Step 1: Basic file validation
    const validation = validateAvatarFile(file);
    if (!validation.valid) {
      return { success: false, error: validation.error };
    }

    // Step 2: Verify file magic numbers (prevents fake file extensions)
    const magicNumberCheck = await verifyFileMagicNumbers(file);
    if (!magicNumberCheck.valid) {
      return { success: false, error: magicNumberCheck.error };
    }

    // Step 3: Validate image dimensions (prevents DoS attacks + ensures usable avatar)
    const dimensionCheck = await validateImageDimensions(file);
    if (!dimensionCheck.valid) {
      return { success: false, error: dimensionCheck.error };
    }

    console.log(`✅ Avatar validation passed: ${dimensionCheck.width}x${dimensionCheck.height}, type: ${magicNumberCheck.detectedType}`);

    // Get file extension (use detected type instead of trusting extension)
    const fileExt = magicNumberCheck.detectedType.split('/')[1];
    const storagePath = `${playerId}.${fileExt === 'jpeg' ? 'jpg' : fileExt}`;

    // Optional: Resize image to save storage space
    let uploadFile = file;
    if (options.resize !== false) {
      try {
        const resizedBlob = await resizeImage(
          file, 
          options.maxDimension || 400, 
          options.quality || 0.9
        );
        uploadFile = new File([resizedBlob], file.name, { type: 'image/jpeg' });
      } catch (resizeError) {
        console.warn('Avatar resize failed, uploading original:', resizeError);
        // Continue with original file if resize fails
      }
    }

    // Upload to Supabase Storage
    const { data: uploadData, error: uploadError } = await supabase
      .storage
      .from(PLAYER_AVATARS_BUCKET)
      .upload(storagePath, uploadFile, {
        cacheControl: '300', // 5 minutes
        upsert: true // Overwrite if exists
      });

    if (uploadError) {
      console.error('Storage upload error:', uploadError);
      return { success: false, error: uploadError.message };
    }

    // Update players table with avatar path
    const { error: updateError } = await supabase
      .from('players')
      .update({ avatar_url: storagePath })
      .eq('id', playerId);

    if (updateError) {
      console.error('Database update error:', updateError);
      // Note: File is uploaded but DB update failed
      return { 
        success: false, 
        error: `File uploaded but failed to update database: ${updateError.message}` 
      };
    }

    // Get public URL
    const publicUrl = getPlayerAvatarPublicUrl(storagePath);

    return {
      success: true,
      path: storagePath,
      publicUrl
    };

  } catch (error) {
    console.error('Upload player avatar error:', error);
    return { 
      success: false, 
      error: error.message || 'Unknown error during upload' 
    };
  }
}

/**
 * Remove a player avatar
 * @param {string} playerId - Player UUID
 * @param {string} path - Current avatar storage path
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export async function removePlayerAvatar(playerId, path) {
  try {
    // Delete from storage if path exists
    if (path && path.trim() !== '') {
      const { error: deleteError } = await supabase
        .storage
        .from(PLAYER_AVATARS_BUCKET)
        .remove([path]);

      if (deleteError) {
        console.warn('Storage delete warning:', deleteError);
        // Continue even if delete fails (file might not exist)
      }
    }

    // Update database to set avatar_url to null
    const { error: updateError } = await supabase
      .from('players')
      .update({ avatar_url: null })
      .eq('id', playerId);

    if (updateError) {
      console.error('Database update error:', updateError);
      return { 
        success: false, 
        error: `Failed to update database: ${updateError.message}` 
      };
    }

    return { success: true };

  } catch (error) {
    console.error('Remove player avatar error:', error);
    return { 
      success: false, 
      error: error.message || 'Unknown error during removal' 
    };
  }
}
